import React, {
  useContext, useEffect, useReducer, useRef, useState
} from 'react';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { withErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { withHydrator } from '@thd-olt-component-react/hydrator';
import { Placeholder } from '@thd-olt-component-react/core-ui';
import { withDynamicComponent } from '@thd-nucleus/app-render';
import { useDataModel } from '@thd-nucleus/data-sources';
import { MobileOverlay } from '../Mobile/MobileOverlay';
import { useProductReviews } from '../useProductReviews';
import { PresentationProvider } from '../../context/PresentationProvider';
import { dataModel } from '../ReviewDataModel';
import { initialState } from '../../constants';
import { stateReducer } from '../../reducer';
import { RatingsAndReviewsPropTypes } from '../RatingsAndReviewsPropTypes';
import { RatingsAndReviewsPlaceholder } from '../RatingsAndReviewsPlaceholder';
import {
  getImageUrl,
  getProductReviews,
  getReviewStatistics,
  getSelectedSentiment,
  getUpdatedTotalPage,
  onFilterChange,
  shouldNotRenderAccordionBody,
  shouldRenderFirstReview,
  shouldRenderPlaceholder
} from '../../helpers';
import { FirstReview } from '../FirstReview/FirstReview.component';
import { RatingsAndReviewsDesktop } from '../RatingsAndReviewsDesktop';
import { track } from '../../ratings-reviews-analytics';
import '../ratings-and-reviews.style.scss';

const RatingsAndReviewsAccordionBody = ({
  apiKey = 'x5w9jA8tWVGcqRhujrHTvjRwQfH8MkFc',
  itemId = null,
  prosAndCons = false,
  seoPageNumber = null,
  category = 'product',
  disableOverlayOnClick = false,
  forceMobile = false,
  showProductAttribution = false,
  noPipBackground = false
}) => {

  const { channel } = useContext(ExperienceContext);
  const isDesktop = channel === 'desktop';
  const [state, dispatch] = useReducer(stateReducer, initialState);
  const persistedStatistics = useRef({});
  const initialReviews = useRef({});
  const persistedPageContext = useRef();
  const carouselRef = useRef();
  const searchAndSortRef = useRef();

  const [selectedReviewSummaryAspect, setSelectedAspect] = useState(null);

  const { data } = useDataModel('product', {
    variables: {
      itemId
    },
    skip: category === 'store'
  });

  const { data: summaryData, loading: summaryLoading } = useDataModel('reviewSummary', {
    variables: {
      itemId,
      modelName: 'combined_review_summary'
    }
  });

  useEffect(() => {
    if (!summaryLoading) {
      track('accordion-open',
        !!summaryData?.reviewSummary
      );
    }
  }, [summaryData]);

  useEffect(() => {
    if (seoPageNumber) {
      dispatch({ type: 'sort', value: 'photoreview' });
      dispatch({ type: 'pageLoad', value: seoPageNumber });
    }
  }, [seoPageNumber]);

  const {
    averageRating, FilterSelected, loading, reviewModels = [], statistics, totalReviews
  } = useProductReviews({ itemId, seoPageNumber, ...state });

  useEffect(() => {
    if (!persistedPageContext.current) {
      persistedPageContext.current = state.pageContext;
      return;
    }
    if (persistedPageContext.current.currentPage !== state.pageContext.currentPage) {
      persistedPageContext.current = state.pageContext;
      if (carouselRef.current) {
        carouselRef.current.scrollIntoView({
          // do not use smooth. It'll cause the page to scroll to the bottom while it updates the results.
          behavior: 'auto'
        });
      }
    }
  }, [state.pageContext]);

  const {
    filters, pageContext, searchText, selectedSentiment, sortBy
  } = state;

  useEffect(() => {
    if (!(searchText === selectedReviewSummaryAspect)) {
      setSelectedAspect(null);
    }
  }, [searchText]);

  const onAspectClick = (__, aspect) => {
    setSelectedAspect(aspect);
    if (window) {
      searchAndSortRef.current.scrollIntoView();
    }

    dispatch({ type: 'search', value: aspect });
  };

  const hasProductData = averageRating || totalReviews;
  const sentimentChange = (sentiment) => dispatch({ type: 'sentiment', value: sentiment });

  if (shouldRenderPlaceholder(loading, persistedStatistics, itemId)) {
    return (
      <RatingsAndReviewsPlaceholder
        color="#E0E0E0"
        showLoadingAnimation
      />
    );
  }
  const selected = getSelectedSentiment(selectedSentiment, filters);
  let productReviews = getProductReviews({
    reviewModels, initialReviews, itemId, searchText, selected
  });
  if (shouldNotRenderAccordionBody(hasProductData, productReviews, persistedStatistics, itemId)) {
    return null;
  }
  const updatedTotalpage = getUpdatedTotalPage(selectedSentiment, statistics?.totalPages);
  if (state.pageContext.totalPages !== updatedTotalpage) {
    dispatch({ type: 'totalPages', value: updatedTotalpage });
  }
  const { SearchText } = FilterSelected || {};
  if (persistedStatistics.current && !persistedStatistics.current[itemId]) {
    persistedStatistics.current[itemId] = statistics;
    initialReviews.current[itemId] = reviewModels;
  }
  const reviewStatistics = getReviewStatistics(persistedStatistics, statistics, itemId);
  const noResults = state.searchText && reviewModels.length === 0 && !loading;

  const { product } = data || {};
  const { identifiers, media } = product || {};
  const { brandName = '', productLabel = '' } = identifiers || {};
  const imageUrl = getImageUrl(media);

  if (isDesktop && !forceMobile) {
    return (
      <RatingsAndReviewsDesktop
        itemId={itemId}
        isUsingAccordion
        disableOverlayOnClick={disableOverlayOnClick}
        productLabel={productLabel}
        searchAndSortRef={searchAndSortRef}
        selectedReviewSummaryAspect={selectedReviewSummaryAspect}
        setSelectedAspect={setSelectedAspect}
        showProductAttribution={showProductAttribution}
        noPipBackground={noPipBackground}
      />
    );
  }

  if (shouldRenderFirstReview(category, reviewModels, reviewStatistics)) {
    return (
      <PresentationProvider useCondensedLayout itemId={itemId}>
        <FirstReview
          reviewStatistics={reviewStatistics}
          itemId={itemId}
          apiKey={apiKey}
          className="outline"
          showHeading={false}
        />
      </PresentationProvider>
    );
  }

  return (
    <PresentationProvider useCondensedLayout itemId={itemId} showProductAttribution={showProductAttribution}>
      <div className="ratings-reviews__accordion-body-content" data-component="RatingsAndReviewsAccordionBody">
        <MobileOverlay
          apiKey={apiKey}
          itemId={itemId}
          reviewModels={reviewModels}
          onDeselectFilter={(checkbox) => dispatch({ type: 'removeFilter', value: checkbox })}
          onFilterChange={(updatedFilters) => onFilterChange(updatedFilters, { dispatch, useCondensedLayout: true })}
          onPageChange={(updatedPageContext) => dispatch({
            type: 'pageChange',
            value: updatedPageContext
          })}
          filterCheckboxes={filters}
          reviewStatistics={reviewStatistics}
          selected={selected}
          selectedSentiment={selectedSentiment}
          brandName={brandName}
          imageUrl={imageUrl}
          productLabel={productLabel}
          prosAndCons={prosAndCons}
          onSearch={(searchTerm) => {
            if (searchTerm) {
              dispatch({ type: 'search', value: searchTerm });
              dispatch({ type: 'sort', value: 'photoreview' });
            }
          }}
          onSearchClear={() => dispatch({ type: 'search', value: null })}
          onSentimentChange={sentimentChange}
          searchTerm={SearchText}
          onSortChange={(updatedSortValue) => {
            dispatch({ type: 'sort', value: updatedSortValue });
            dispatch({ type: 'search', value: null });
          }}
          sortBy={sortBy}
          noResults={noResults}
          loading={loading}
          pageContext={pageContext}
          statistics={statistics}
          category={category}
          seoPageNumber={seoPageNumber}
          carouselRef={carouselRef}
          searchAndSortRef={searchAndSortRef}
          selectedReviewSummaryAspect={selectedReviewSummaryAspect}
          onAspectClick={onAspectClick}
        />
      </div>
    </PresentationProvider>
  );
};

RatingsAndReviewsAccordionBody.displayName = 'RatingsAndReviewsAccordionBody';

RatingsAndReviewsAccordionBody.propTypes = RatingsAndReviewsPropTypes;

RatingsAndReviewsAccordionBody.dataModel = dataModel;

const HydratedComponent = withHydrator({
  id: 'product-section-ratings-reviews',
  scrollBuffer: 750,
  delay: 5000,
  placeholder: (
    <div data-component="RatingsAndReviewsAccordionBodyPlaceholder">
      <Placeholder
        width="100%"
        height="1500px"
        color="#F5F5F5"
      />
    </div>
  )
}, RatingsAndReviewsAccordionBody);
const DynamicComponent = withDynamicComponent(HydratedComponent);
const ErrorComponent = withErrorBoundary(DynamicComponent);

export { ErrorComponent as RatingsAndReviewsAccordionBody };
